import { useQuery } from '@tanstack/react-query'
import { resourcesCopy } from '@/copy/resources'
import { Field, SelectField, Textarea } from '@/ui/Field/Field'
import { recipesApi } from '../../api/recipes'
import type { FieldProps, Fields, ResourceFormDescriptor } from './shared'

/** Form-state key holding the manifest as pretty-printed JSON. */
const MANIFEST = 'manifest'

/**
 * A workspace recipe: its manifest as JSON, the same format the shipped
 * recipes use — start from one of them and change what it installs. The
 * server checks the manifest on save.
 */
export function RecipeFields({ fields, set, isEdit }: FieldProps) {
  const copy = resourcesCopy.forms.recipe
  const { data: shipped = [] } = useQuery({
    queryKey: ['shipped-recipes'],
    queryFn: recipesApi.shipped,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const startFrom = (id: string) => {
    const recipe = shipped.find((r) => r.id === id)
    if (!recipe) return
    // A workspace recipe is known by its resource, not a manifest id.
    const manifest = { ...recipe.manifest }
    delete manifest.id
    set(MANIFEST, JSON.stringify(manifest, null, 2))
  }

  return (
    <>
      {!isEdit && (
        <SelectField
          id="recipe-start"
          label={copy.startFrom}
          value=""
          onValue={startFrom}
        >
          <option value="">{copy.blank}</option>
          {shipped.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title}
            </option>
          ))}
        </SelectField>
      )}
      <Field id="recipe-manifest" label={copy.manifest}>
        <Textarea
          mono
          value={fields[MANIFEST] ?? ''}
          onChange={(e) => set(MANIFEST, e.target.value)}
          rows={24}
          spellCheck={false}
        />
      </Field>
    </>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- descriptor is co-located with its component
export const recipeForm: ResourceFormDescriptor = {
  Fields: RecipeFields,
  fromResource: (d: unknown) => ({
    [MANIFEST]: JSON.stringify(d ?? {}, null, 2),
  }),
  toBody: (f: Fields) => {
    const text = (f[MANIFEST] ?? '').trim()
    if (!text) throw new Error(resourcesCopy.forms.recipe.empty)
    try {
      return JSON.parse(text)
    } catch (e) {
      throw new Error(
        resourcesCopy.forms.recipe.invalidJson((e as Error).message),
        { cause: e },
      )
    }
  },
}
